/**
 * Result statistics: yield with uncertainty, capability indices, normality,
 * and the two sensitivity measures that come free from the main run.
 */

import { normalCdf } from './special.ts';

// --- Yield -----------------------------------------------------------------

export interface YieldEstimate {
  /** Successes / trials. */
  point: number;
  lower: number;
  upper: number;
  /** Defects per million, from `point`. */
  ppm: number;
  ppmLower: number;
  ppmUpper: number;
  failures: number;
  trials: number;
  /**
   * True when too few failures were observed for the point estimate to mean
   * anything. The UI must show the bound instead of the point (PRD §4.7).
   */
  belowResolution: boolean;
}

/** Minimum observed failures before a defect-rate point estimate is meaningful. */
export const MIN_FAILURES_FOR_POINT_ESTIMATE = 10;

/**
 * Wilson score interval.
 *
 * Chosen over the textbook normal approximation because at the extreme
 * proportions this tool lives at (0.999999 and up) the Wald interval is not
 * merely imprecise — it produces bounds above 1 and collapses to zero width
 * when zero failures are observed, which is exactly the case where honest
 * uncertainty matters most.
 */
export function wilsonYield(successes: number, trials: number, z = 1.959963984540054): YieldEstimate {
  const failures = trials - successes;
  if (trials === 0) {
    return {
      point: NaN, lower: NaN, upper: NaN,
      ppm: NaN, ppmLower: NaN, ppmUpper: NaN,
      failures: 0, trials: 0, belowResolution: true,
    };
  }

  const p = successes / trials;
  const z2 = z * z;
  const denom = 1 + z2 / trials;
  const centre = (p + z2 / (2 * trials)) / denom;
  const half = (z * Math.sqrt((p * (1 - p)) / trials + z2 / (4 * trials * trials))) / denom;

  const lower = Math.max(0, centre - half);
  const upper = Math.min(1, centre + half);

  return {
    point: p,
    lower,
    upper,
    ppm: (1 - p) * 1e6,
    // A high yield bound is a low defect bound: the interval flips.
    ppmLower: (1 - upper) * 1e6,
    ppmUpper: (1 - lower) * 1e6,
    failures,
    trials,
    belowResolution: failures < MIN_FAILURES_FOR_POINT_ESTIMATE,
  };
}

// --- Capability ------------------------------------------------------------

export interface Capability {
  cp?: number;
  cpk?: number;
  cpu?: number;
  cpl?: number;
  /** Yield implied by the normal model — differs from empirical yield when the output is not normal. */
  normalYield?: number;
}

export function capability(
  mean: number,
  stdDev: number,
  lsl?: number,
  usl?: number,
): Capability {
  if (!(stdDev > 0)) return {};

  const cpu = usl !== undefined ? (usl - mean) / (3 * stdDev) : undefined;
  const cpl = lsl !== undefined ? (mean - lsl) / (3 * stdDev) : undefined;

  let cp: number | undefined;
  if (lsl !== undefined && usl !== undefined) cp = (usl - lsl) / (6 * stdDev);

  const candidates = [cpu, cpl].filter((v): v is number => v !== undefined);
  const cpk = candidates.length > 0 ? Math.min(...candidates) : undefined;

  let normalYield: number | undefined;
  if (lsl !== undefined || usl !== undefined) {
    const above = usl !== undefined ? 1 - normalCdf((usl - mean) / stdDev) : 0;
    const below = lsl !== undefined ? normalCdf((lsl - mean) / stdDev) : 0;
    normalYield = 1 - above - below;
  }

  return { cp, cpk, cpu, cpl, normalYield };
}

// --- Normality -------------------------------------------------------------

export interface NormalityResult {
  k2: number;
  pValue: number;
  /** p < 0.05: the departure from normal is larger than sampling noise. */
  rejected: boolean;
  /**
   * How far the departure moves the ±3σ point, in units of σ.
   *
   * A Cornish–Fisher expansion of the normal quantile: at z = ±3 the skewness
   * term contributes (z²−1)/6 = 1.333 and the kurtosis term (z³−3z)/24 = 0.75,
   * with the signs differing between the two tails, so the larger shift is
   * 1.333·|skew| + 0.75·|excess kurtosis|.
   */
  quantileShift: number;
  /**
   * True when the output is non-normal *enough to change a number you would
   * act on* — statistically rejected AND shifting the 3σ point by more than
   * `PRACTICAL_SHIFT`.
   *
   * This is the field the UI should warn on. `rejected` alone is not usable at
   * these sample sizes: with 10^5 samples the standard error on excess
   * kurtosis is ~0.015, so a kurtosis of 0.04 — a distribution that is normal
   * to three decimal places — is a 2.6σ result and rejects. Warning on that
   * tells a user to distrust Cp/Cpk on a perfectly normal output, which trains
   * them to ignore the warning that matters.
   */
  matters: boolean;
}

/**
 * How far the ±3σ point must move before non-normality is worth reporting.
 *
 * 0.1σ is about 3% of a 3σ limit. Below that, the capability indices are
 * wrong by less than the uncertainty in the tolerances that fed them.
 */
export const PRACTICAL_SHIFT = 0.1;

/**
 * D'Agostino-Pearson K^2 omnibus test on skewness and kurtosis.
 *
 * Purpose here is not hypothesis testing for its own sake — it is to decide
 * whether Cp/Cpk may be reported without a warning. Sums of uniforms and any
 * nonlinear transfer function routinely fail it, and a Cpk computed on a
 * visibly non-normal output is a number that means nothing (PRD §4.7).
 *
 * Note: at n = 10^6 the test rejects on deviations far too small to matter
 * practically. Callers should weigh `rejected` alongside the actual skew and
 * excess kurtosis rather than treating it as a verdict.
 */
export function normalityTest(n: number, skewness: number, kurtosis: number): NormalityResult {
  if (n < 20) return { k2: NaN, pValue: NaN, rejected: false, quantileShift: 0, matters: false };

  // Skewness -> Z1 (D'Agostino 1970)
  const y = skewness * Math.sqrt(((n + 1) * (n + 3)) / (6 * (n - 2)));
  const beta2 =
    (3 * (n * n + 27 * n - 70) * (n + 1) * (n + 3)) /
    ((n - 2) * (n + 5) * (n + 7) * (n + 9));
  const w2 = -1 + Math.sqrt(2 * (beta2 - 1));
  const delta = 1 / Math.sqrt(Math.log(Math.sqrt(w2)));
  const alpha = Math.sqrt(2 / (w2 - 1));
  const z1 = y === 0 ? 0 : delta * Math.log(y / alpha + Math.sqrt((y / alpha) ** 2 + 1));

  // Kurtosis -> Z2 (Anscombe-Glynn 1983)
  const b2 = kurtosis + 3;
  const eB2 = (3 * (n - 1)) / (n + 1);
  const varB2 = (24 * n * (n - 2) * (n - 3)) / ((n + 1) * (n + 1) * (n + 3) * (n + 5));
  const x = (b2 - eB2) / Math.sqrt(varB2);
  const sqrtBeta1 =
    ((6 * (n * n - 5 * n + 2)) / ((n + 7) * (n + 9))) *
    Math.sqrt((6 * (n + 3) * (n + 5)) / (n * (n - 2) * (n - 3)));
  const a = 6 + (8 / sqrtBeta1) * (2 / sqrtBeta1 + Math.sqrt(1 + 4 / (sqrtBeta1 * sqrtBeta1)));
  const term = (1 - 2 / a) / (1 + x * Math.sqrt(2 / (a - 4)));
  const z2 =
    (1 - 2 / (9 * a) - Math.cbrt(term)) / Math.sqrt(2 / (9 * a));

  const k2 = z1 * z1 + z2 * z2;
  // Chi-square with 2 degrees of freedom has survival function exp(-x/2).
  const pValue = Number.isFinite(k2) ? Math.exp(-k2 / 2) : NaN;
  const rejected = Number.isFinite(pValue) && pValue < 0.05;

  const quantileShift = (4 / 3) * Math.abs(skewness) + 0.75 * Math.abs(kurtosis);

  return {
    k2,
    pValue,
    rejected,
    quantileShift,
    matters: rejected && quantileShift > PRACTICAL_SHIFT,
  };
}

// --- Rank correlation ------------------------------------------------------

/** Average ranks, ties shared. */
export function rank(values: Float64Array | number[], n: number): Float64Array {
  const idx = new Int32Array(n);
  for (let i = 0; i < n; i++) idx[i] = i;
  const arr = values;
  const order = Array.from(idx).sort((a, b) => arr[a] - arr[b]);

  const ranks = new Float64Array(n);
  let i = 0;
  while (i < n) {
    let j = i;
    while (j + 1 < n && arr[order[j + 1]] === arr[order[i]]) j++;
    const avg = (i + j) / 2 + 1;
    for (let k = i; k <= j; k++) ranks[order[k]] = avg;
    i = j + 1;
  }
  return ranks;
}

export function pearson(a: Float64Array, b: Float64Array, n: number): number {
  let ma = 0;
  let mb = 0;
  for (let i = 0; i < n; i++) {
    ma += a[i];
    mb += b[i];
  }
  ma /= n;
  mb /= n;

  let sab = 0;
  let saa = 0;
  let sbb = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i] - ma;
    const db = b[i] - mb;
    sab += da * db;
    saa += da * da;
    sbb += db * db;
  }
  if (saa === 0 || sbb === 0) return 0;
  return sab / Math.sqrt(saa * sbb);
}

/** Spearman rank correlation, computed on the retained subsample. */
export function spearman(a: Float64Array, b: Float64Array, n: number): number {
  return pearson(rank(a, n), rank(b, n), n);
}

// --- Linear solve for regression coefficients ------------------------------

/**
 * Solve a symmetric positive-definite system by Cholesky decomposition.
 * Returns null when the matrix is not positive definite, which for a
 * covariance matrix means collinear or zero-variance inputs.
 */
export function choleskySolve(a: Float64Array, b: Float64Array, n: number): Float64Array | null {
  const l = new Float64Array(n * n);

  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = a[i * n + j];
      for (let k = 0; k < j; k++) sum -= l[i * n + k] * l[j * n + k];
      if (i === j) {
        if (!(sum > 1e-300)) return null;
        l[i * n + j] = Math.sqrt(sum);
      } else {
        l[i * n + j] = sum / l[j * n + j];
      }
    }
  }

  const y = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    let sum = b[i];
    for (let k = 0; k < i; k++) sum -= l[i * n + k] * y[k];
    y[i] = sum / l[i * n + i];
  }

  const x = new Float64Array(n);
  for (let i = n - 1; i >= 0; i--) {
    let sum = y[i];
    for (let k = i + 1; k < n; k++) sum -= l[k * n + i] * x[k];
    x[i] = sum / l[i * n + i];
  }
  return x;
}
